import React from 'react';

/**
 * Gecko nest color scheme.
 */
const colors = Object.freeze({
    black: '#000000',
    white: '#FFFFFF',
    transparent: 'rgba(0, 0, 0, 0)',
    neutral: [
        '',
        '#2A2B2E',
        '#607D8B',
        '#A2BAC3',
        '#FBF9F8',
        '#FFFFFF',
        '#ECEFF1',
        '#F5F5F5',
        '#F5F5F5',
        '#EEEEEE',
        '#BDBDBD',
    ],
    surface: ['', '#1B1F35', 'rgba(0, 0, 0, 0.38)', 'rgba(255, 255, 255, 0.38)'],
    blue: ['', '#3F51B5', '#2196F3', '#E8F4FD'],
    darkBlue: ['', '#111630', '#212A5C', '#283472', '#303E88', '#38489E'],
    green: ['', '#6AAC29', '#4CAF50', '#29AC6B', '#009688', '#E8F5E9'],
    red: ['', '#F44336', '#FF745F', '#FEEBEE'],
    amber: ['', '#A16D37', '#A16D37', '#A16D37', '#FFF3E0'],
} as const);

/**
 * Default transition speeds in milliseconds to be used throughout the application.
 */
const transitions = {
    default: 0.3,
    fast: 0.15,
    slow: 0.6,
};

/**
 * List of breakpoints used by the application.
 */
const breakpoints = {
    large: { min: 1281, max: Infinity },
    medium: { min: 1079, max: 1280 },
    small: { min: 769, max: 1024 },
    tiny: { min: 0, max: 768 },
};

// application fonts with fallbacks
const manropeFont = 'Manrope, sans-serif';
const robotoFont = 'Roboto, sans-serif';
const interFont = 'Inter, sans-serif';

/**
 * Gecko nest typography scheme.
 *
 * manrope: https://fonts.googleapis.com/css?family=Manrope:400,500,600,700&display=swap
 */
const fonts = {
    h1: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 500,
        letterSpacing: 'normal',
        fontSize: 56,
        lineHeight: 1.35,
    } as React.CSSProperties,
    h2: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 'bold',
        letterSpacing: 'normal',
        fontSize: 40,
        lineHeight: 1.35,
    } as React.CSSProperties,
    h3: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 500,
        letterSpacing: -0.25,
        fontSize: 32,
        lineHeight: 1.2,
    } as React.CSSProperties,
    h4: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 'bold',
        letterSpacing: -0.5,
        fontSize: 24,
        lineHeight: 1.2,
    } as React.CSSProperties,
    h5: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 'bold',
        letterSpacing: 'normal',
        fontSize: 16,
        lineHeight: 1.2,
    } as React.CSSProperties,
    h6: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 600,
        letterSpacing: 0.5,
        fontSize: 12,
        lineHeight: 1.16,
    } as React.CSSProperties,
    paragraph: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 'normal',
        letterSpacing: 0.5,
        fontSize: 16,
        lineHeight: 1.5,
    } as React.CSSProperties,
    label: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 'normal',
        letterSpacing: 'normal',
        fontSize: 14,
        lineHeight: 1.6,
    } as React.CSSProperties,
    labelSemiBold: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 600,
        letterSpacing: 'normal',
        fontSize: 14,
        lineHeight: 1.6,
    } as React.CSSProperties,
    caption: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 500,
        letterSpacing: 'normal',
        fontSize: 12,
        lineHeight: 1.35,
    } as React.CSSProperties,
    button: {
        fontFamily: manropeFont,
        fontStyle: 'normal',
        fontWeight: 600,
        letterSpacing: 'normal',
        fontSize: 14,
        lineHeight: 1.35,
    } as React.CSSProperties,
};

/**
 * Gecko nest old typography scheme.
 * This typography definitions will be replaced with the definitions defined above.
 *
 * Font cdns:
 * roboto: https://fonts.googleapis.com/css?family=Roboto:300,400,500,700&display=swap
 * inter: https://fonts.googleapis.com/css?family=Inter:300,400,500,700&display=swap
 */
const typography = Object.freeze({
    displays: {
        d1: {
            fontFamily: robotoFont,
            fontSize: 48,
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '58px',
            letterSpacing: 0.5,
            textAlign: 'left',
        } as React.CSSProperties,
        d2: {
            fontFamily: robotoFont,
            fontSize: 29,
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '29px',
            letterSpacing: -0.2,
            textAlign: 'left',
        } as React.CSSProperties,
    },
    page: {
        title: {
            fontFamily: robotoFont,
            fontSize: 34,
            fontStyle: 'normal',
            fontWeight: 300,
            lineHeight: '41px',
            letterSpacing: 0.5,
            textAlign: 'left',
        },
    },
    headings: {
        h1: {
            fontFamily: robotoFont,
            fontSize: 29,
            fontStyle: 'normal',
            fontWeight: 900,
            lineHeight: '32px',
            letterSpacing: -0.2,
            textAlign: 'left',
            marginBottom: 20,
        } as React.CSSProperties,
        h2: {
            fontFamily: robotoFont,
            fontSize: 23,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '26px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        h3: {
            fontFamily: robotoFont,
            fontSize: 20,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '23px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        h4: {
            fontFamily: robotoFont,
            fontSize: 18,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '20px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        h5: {
            fontFamily: robotoFont,
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '18px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 8,
        } as React.CSSProperties,
        h6: {
            fontFamily: robotoFont,
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '13px',
            letterSpacing: 1.6,
            textAlign: 'left',
            marginBottom: 8,
        } as React.CSSProperties,
    },
    paragraphs: {
        h1: {
            fontFamily: interFont,
            fontSize: 29,
            fontStyle: 'normal',
            fontWeight: 800,
            lineHeight: '35px',
            letterSpacing: -0.2,
            textAlign: 'left',
            marginBottom: 20,
        } as React.CSSProperties,
        h2: {
            fontFamily: interFont,
            fontSize: 23,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '27px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        h3: {
            fontFamily: interFont,
            fontSize: 20,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        h4: {
            fontFamily: interFont,
            fontSize: 18,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '22px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        h5: {
            fontFamily: interFont,
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '19px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 8,
        } as React.CSSProperties,
        h6: {
            fontFamily: interFont,
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: '14px',
            letterSpacing: 1.6,
            textAlign: 'left',
            marginBottom: 8,
        } as React.CSSProperties,
    },
    content: {
        text: {
            fontFamily: robotoFont,
            fontSize: 16,
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: 0,
            textAlign: 'left',
            marginBottom: 16,
        } as React.CSSProperties,
        caption: {
            paragraph: {
                fontFamily: robotoFont,
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '18px',
                letterSpacing: 0.1,
                textAlign: 'left',
            } as React.CSSProperties,
            default: {
                fontFamily: interFont,
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '14px',
                letterSpacing: 0.1,
                textAlign: 'left',
            } as React.CSSProperties,
            medium: {
                fontFamily: interFont,
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '14px',
                letterSpacing: 0.1,
                textAlign: 'left',
            } as React.CSSProperties,
            strong: {
                fontFamily: interFont,
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: '14px',
                letterSpacing: 0.1,
                textAlign: 'left',
            } as React.CSSProperties,
            stronger: {
                fontFamily: interFont,
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '14px',
                letterSpacing: 0.1,
                textAlign: 'left',
            } as React.CSSProperties,
        },
        label: {
            default: {
                fontFamily: robotoFont,
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '16px',
                letterSpacing: 0,
                textAlign: 'left',
            } as React.CSSProperties,
            xs: {
                fontFamily: robotoFont,
                fontSize: 10,
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '16px',
                letterSpacing: 0,
                textAlign: 'left',
            } as React.CSSProperties,
            small: {
                fontFamily: robotoFont,
                fontSize: 12,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: 0,
                textAlign: 'left',
            } as React.CSSProperties,
            medium: {
                fontFamily: robotoFont,
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '16px',
                letterSpacing: 0,
                textAlign: 'left',
            } as React.CSSProperties,
            strong: {
                fontFamily: robotoFont,
                fontSize: 14,
                fontStyle: 'normal',
                fontWeight: 700,
                lineHeight: '16px',
                letterSpacing: 0,
                textAlign: 'left',
            } as React.CSSProperties,
        },
    },
} as const);

export const theme = { colors, transitions, breakpoints, fonts, typography };
